import { useCallback } from "react";
import { shiftApi } from "../../../api/resources";
import type { ShiftAdmin, ShiftQuery } from "../../../api/types";
import { DemoBadge } from "../../../components/ui";
import { DataTable, LoadState, useLoad } from "../shared";
import { AttentionIcon, FLAG_LABEL, ShiftStatusPill, fmtWorkDate, timeRange } from "./shiftUi";

interface Props {
  query: ShiftQuery;
  reload: number;
  onOpen: (id: string) => void;
  testId: string;
  showFlags?: boolean;
}

export function ShiftTable({ query, reload, onOpen, testId, showFlags }: Props) {
  const key = JSON.stringify(query);
  const loader = useCallback(() => shiftApi.list(JSON.parse(key) as ShiftQuery), [key, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<ShiftAdmin[]>(loader);
  return (
    <>
      <LoadState error={error} loading={!data && !error} testId={`${testId}-error`} />
      {data && (
        <DataTable testId={testId} headers={["Date", "Venue-local time", "Employee", "Venue", "Role", "Status", showFlags ? "Needs attention" : ""]} empty={data.length === 0}>
          {data.map((s) => (
            <tr key={s.id} data-testid={`shift-row-${s.id}`} onClick={() => onOpen(s.id)} className="cursor-pointer align-top hover:bg-white/[0.04]">
              <td className="whitespace-nowrap px-5 py-4 text-white">
                <button type="button" data-testid={`shift-open-${s.id}`} className="text-left hover:text-signal">{fmtWorkDate(s.work_date)}</button>
                {s.is_past && <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-600">Past</span>}
              </td>
              <td className="whitespace-nowrap px-5 py-4 text-zinc-300" data-testid={`shift-time-${s.id}`}>{timeRange(s)}</td>
              <td className="px-5 py-4 text-zinc-200">{s.employee_name} <DemoBadge show={s.is_demo} testId={`shift-demo-badge-${s.id}`} /></td>
              <td className="px-5 py-4 text-zinc-300">{s.venue_name}</td>
              <td className="px-5 py-4 text-zinc-400">{s.role ?? "—"}</td>
              <td className="px-5 py-4"><ShiftStatusPill status={s.status} testId={`shift-status-${s.id}`} /></td>
              <td className="px-5 py-4">
                {showFlags ? (
                  <ul className="space-y-1">
                    {s.attention_flags.filter((f) => !f.resolved_at).map((f) => (
                      <li key={f.id} data-testid={`shift-flag-${s.id}-${f.code}`} className="flex items-center gap-2 text-xs text-red-200">
                        <AttentionIcon show testId={`shift-flag-icon-${f.id}`} /> {FLAG_LABEL[f.code]}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <AttentionIcon show={s.needs_attention} testId={`shift-attention-${s.id}`} />
                )}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </>
  );
}
